import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { D, designError, fmtDate, label, useDesignMe } from "@/lib/designApi";
import {
  DataTable, ErrorList, Field, FilesPanel, HistoryPanel, NoAccess, ObsoleteButton, PageHeader, Section, SignDialog,
  SimpleSelect, StatusBadge, WorkflowBar, useQueryId,
} from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, CloudUpload, Plus, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "", description: "", uom: "PCS", make_buy: "make", material: "", size: "",
  specification: "", weight_kg: 0, std_cost: 0, hsn_code: "", customer_part_no: "", number: "" };

function ItemForm({ form, setForm, categories, disabled, showNumber }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Name *" className="sm:col-span-2"><Input value={form.name} onChange={set("name")} disabled={disabled} data-testid="item-name" /></Field>
      <Field label="Category *">
        <SimpleSelect value={form.category} onChange={set("category")} disabled={disabled} testid="item-category"
          options={categories.map((c) => ({ value: c.code, label: `${c.label} (${c.prefix}-…)` }))} />
      </Field>
      <Field label="Make / Buy">
        <SimpleSelect value={form.make_buy} onChange={set("make_buy")} disabled={disabled} options={["make", "buy", "job_work"]} />
      </Field>
      <Field label="Material / Grade"><Input value={form.material} onChange={set("material")} disabled={disabled} /></Field>
      <Field label="Size / Dimensions"><Input value={form.size} onChange={set("size")} disabled={disabled} /></Field>
      <Field label="Specification / Standard"><Input value={form.specification} onChange={set("specification")} disabled={disabled} /></Field>
      <Field label="UOM"><Input value={form.uom} onChange={set("uom")} disabled={disabled} /></Field>
      <Field label="Std cost (₹)"><Input type="number" min="0" value={form.std_cost} onChange={set("std_cost")} disabled={disabled} /></Field>
      <Field label="Weight (kg)"><Input type="number" min="0" value={form.weight_kg} onChange={set("weight_kg")} disabled={disabled} /></Field>
      <Field label="HSN code"><Input value={form.hsn_code} onChange={set("hsn_code")} disabled={disabled} /></Field>
      <Field label="Customer part no."><Input value={form.customer_part_no} onChange={set("customer_part_no")} disabled={disabled} /></Field>
      {showNumber && (
        <Field label="Manual / legacy part no. (Design Head only — blank = auto)" className="sm:col-span-2">
          <Input value={form.number} onChange={set("number")} placeholder="Auto-generated" />
        </Field>
      )}
      <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={form.description} onChange={set("description")} disabled={disabled} /></Field>
    </div>
  );
}

function payloadOf(form) {
  return { ...form, weight_kg: Number(form.weight_kg) || 0, std_cost: Number(form.std_cost) || 0, number: form.number || undefined };
}

function DuplicateHints({ form, excludeNumber }) {
  const [cands, setCands] = useState([]);
  useEffect(() => {
    if ((form.name || "").trim().length < 3) { setCands([]); return undefined; }
    const t = setTimeout(() => {
      api.post(`${D}/items/duplicate-check`, { ...form, exclude_number: excludeNumber || "" })
        .then((r) => setCands(r.data.candidates || [])).catch(() => setCands([]));
    }, 350);
    return () => clearTimeout(t);
  }, [form.name, form.material, form.size, form.specification, form.description, excludeNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cands.length) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="duplicate-hints">
      <div className="font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Possible existing parts — reuse instead of creating a duplicate?</div>
      <ul className="mt-2 space-y-1">
        {cands.slice(0, 6).map((c) => (
          <li key={`${c.source}-${c.number}`} className="flex gap-2 items-center">
            <span className="mono font-semibold">{c.number}</span>
            <span className="truncate">{c.name}</span>
            <span className="text-xs text-slate-500">{c.material} {c.size}</span>
            <span className="ml-auto text-xs">{c.source === "erp" ? "ERP master" : <StatusBadge status={c.status} />}</span>
            <span className={`text-xs mono ${c.exact ? "text-red-700 font-bold" : c.probable ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
              {c.exact ? "identical" : `${Math.round(c.score * 100)}%`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ItemDetail({ id, onChanged, onOpen }) {
  const { me, can } = useDesignMe();
  const [rec, setRec] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState([]);
  const [publish, setPublish] = useState(false);
  const [pub, setPub] = useState({ include_bom: true, include_routing: false, update_master_fields: false });
  const nav = useNavigate();
  const load = useCallback(() => {
    api.get(`${D}/items/${id}`).then((r) => { setRec(r.data); setForm({ ...EMPTY, ...r.data, number: "" }); }).catch((e) => toast.error(designError(e)));
  }, [id]);
  useEffect(load, [load]);
  if (!rec) return null;
  const editable = rec.status === "draft" && can("create_edit");
  const save = async () => {
    setErrors([]);
    try {
      const { number, ...rest } = payloadOf(form); // eslint-disable-line no-unused-vars
      const body = {};
      ["name", "category", "description", "uom", "make_buy", "material", "size", "specification", "weight_kg", "std_cost",
        "hsn_code", "customer_part_no"].forEach((k) => { body[k] = rest[k]; });
      await api.patch(`${D}/items/${id}`, { ...body, acknowledge_duplicates: ack });
      toast.success("Saved");
      load();
      onChanged?.();
    } catch (e) {
      setErrors([designError(e)]);
    }
  };
  const changed = () => { load(); onChanged?.(); };
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{rec.number} · Rev {rec.revision} · {label(rec.category)}</div>
        <div className="text-xl font-bold text-slate-900">{rec.name}</div>
      </div>
      <WorkflowBar entity="items" record={rec} onChanged={(n) => (n?.id && n.id !== rec.id ? onOpen?.(n.id) : changed())}
        extra={<>
          <ObsoleteButton entity="items" record={rec} onChanged={changed} />
          {rec.status === "released" && can("publish_erp") && (
            <Button size="sm" variant="outline" onClick={() => setPublish(true)} data-testid="item-publish-erp">
              <CloudUpload className="w-3.5 h-3.5 mr-1" /> Publish to ERP
            </Button>
          )}
        </>} />
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="structure">Drawings · BOM · Routing</TabsTrigger>
          <TabsTrigger value="files">Files ({rec.files?.length || 0})</TabsTrigger>
          <TabsTrigger value="history">Revisions & History</TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="space-y-3">
          <ItemForm form={form} setForm={setForm} categories={me?.config?.item_categories || []} disabled={!editable} />
          {editable && <DuplicateHints form={form} excludeNumber={rec.number} />}
          <ErrorList errors={errors} />
          {editable && (
            <div className="flex items-center gap-3">
              <label className="text-xs flex items-center gap-2"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} />Similar items reviewed — keep as a separate part</label>
              <div className="flex-1" />
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="item-save">Save draft</Button>
            </div>
          )}
          <div className="text-xs text-slate-500 grid sm:grid-cols-3 gap-2">
            <div>Created {fmtDate(rec.created_at)} by {rec.created_by_name || rec.created_by}</div>
            {rec.released_at && <div>Released {fmtDate(rec.released_at)} by {rec.released_by}</div>}
            {rec.erp_item ? <div className="text-emerald-700">In ERP item master ({rec.erp_item.type})</div> : <div>Not in ERP item master</div>}
          </div>
        </TabsContent>
        <TabsContent value="structure" className="space-y-3">
          <Section title="Drawings / documents" right={can("create_edit") && <Button size="sm" variant="outline" onClick={() => nav(`/design/documents?new=1&item=${rec.number}`)}>New drawing</Button>}>
            {!rec.documents?.length ? <div className="text-xs text-slate-500">No drawings linked.</div> : rec.documents.map((d) => (
              <button key={d.id} onClick={() => nav(`/design/documents?id=${d.id}`)} className="flex gap-2 items-center text-sm py-1 hover:underline">
                <span className="mono">{d.number} Rev {d.revision}</span> {d.title} <StatusBadge status={d.status} />
              </button>
            ))}
          </Section>
          <Section title="BOM" right={can("create_edit") && !rec.boms?.length && <Button size="sm" variant="outline" onClick={() => nav(`/design/boms?new=1&item=${rec.number}`)}>Create BOM</Button>}>
            {!rec.boms?.length ? <div className="text-xs text-slate-500">No BOM.</div> : rec.boms.map((b) => (
              <button key={b.id} onClick={() => nav(`/design/boms?id=${b.id}`)} className="flex gap-2 items-center text-sm py-1 hover:underline">
                <span className="mono">{b.number} Rev {b.revision}</span> <StatusBadge status={b.status} />
              </button>
            ))}
          </Section>
          <Section title="Routing" right={can("create_edit") && !rec.routings?.length && <Button size="sm" variant="outline" onClick={() => nav(`/design/routings?new=1&item=${rec.number}`)}>Create routing</Button>}>
            {!rec.routings?.length ? <div className="text-xs text-slate-500">No routing.</div> : rec.routings.map((b) => (
              <button key={b.id} onClick={() => nav(`/design/routings?id=${b.id}`)} className="flex gap-2 items-center text-sm py-1 hover:underline">
                <span className="mono">{b.number} Rev {b.revision}</span> {b.name} <StatusBadge status={b.status} />
              </button>
            ))}
          </Section>
          <Section title={`Where used (${rec.where_used?.length || 0})`}>
            {!rec.where_used?.length ? <div className="text-xs text-slate-500">Not used in any BOM.</div> : (
              <table className="w-full text-sm"><tbody>
                {rec.where_used.map((w) => (
                  <tr key={`${w.bom_id}`} className="border-b border-slate-50">
                    <td className="py-1 mono">{w.parent_no}</td><td>{w.parent_name}</td>
                    <td className="mono">{w.bom_number} Rev {w.bom_revision}</td><td><StatusBadge status={w.bom_status} /></td>
                    <td className="mono">{w.qty}{w.as_alternate ? " (alternate)" : ""}</td>
                  </tr>
                ))}
              </tbody></table>
            )}
          </Section>
          {rec.ecns?.length > 0 && (
            <Section title="Change notices">
              {rec.ecns.map((e) => (
                <button key={e.id} onClick={() => nav(`/design/changes?ecn=${e.id}`)} className="flex gap-2 items-center text-sm py-1 hover:underline">
                  <span className="mono">{e.number}</span> {e.title} <StatusBadge status={e.status} />
                </button>
              ))}
            </Section>
          )}
        </TabsContent>
        <TabsContent value="files">
          <FilesPanel ownerType="items" ownerId={rec.id} files={rec.files} editable={editable} onChanged={load} />
        </TabsContent>
        <TabsContent value="history">
          <HistoryPanel entity="items" record={rec} onOpenRevision={onOpen} />
        </TabsContent>
      </Tabs>
      {publish && (
        <SignDialog open={publish} onOpenChange={setPublish} title={`Publish ${rec.number} Rev ${rec.revision} to ERP`}
          meaning="Creates the part in the ERP Item Master (or links the existing one). Optional: its released BOM and routing."
          onSubmit={async ({ password }) => {
            const { data } = await api.post(`${D}/items/${rec.id}/publish-erp`, { ...pub, password });
            toast.success(`ERP item ${data.item?.action}${data.bom ? ` · BOM ${data.bom.lines} lines` : ""}${data.routing ? ` · routing ${data.routing.action}` : ""}`);
            changed();
          }}>
          {[["include_bom", "Include released BOM (components must already be in ERP)"],
            ["include_routing", "Include released routing (operations on ERP work centres)"],
            ["update_master_fields", "Overwrite name / description / UOM / cost on an existing ERP item"]].map(([k, t]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={pub[k]} onCheckedChange={(v) => setPub((p) => ({ ...p, [k]: !!v }))} />{t}</label>
          ))}
        </SignDialog>
      )}
    </div>
  );
}

export default function DesignItems() {
  const { me, can, error: meErr } = useDesignMe();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [openId, setOpenId] = useState(useQueryId());
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState([]);

  const load = useCallback(() => {
    api.get(`${D}/items`, { params: { q, status, category } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [q, status, category]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("new")) { setCreating(true); }
  }, []);

  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const cats = me?.config?.item_categories || [];

  const create = async () => {
    setErrors([]);
    try {
      const { data } = await api.post(`${D}/items`, { ...payloadOf(form), acknowledge_duplicates: ack });
      toast.success(`Created ${data.number}`);
      setCreating(false);
      setForm(EMPTY);
      setAck(false);
      load();
      setOpenId(data.id);
    } catch (e) {
      setErrors([designError(e)]);
    }
  };

  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-items-page">
      <PageHeader title="Item Master" subtitle="Auto-numbered part master with duplicate search, revisions and controlled release">
        {can("create_edit") && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="design-new-item">
            <Plus className="w-4 h-4 mr-1.5" /> New item
          </Button>
        )}
      </PageHeader>
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative w-72 max-md:w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input className="pl-9" placeholder="Part no, name, material…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="design-items-search" />
        </div>
        <div className="w-44 max-md:flex-1 max-md:min-w-[8rem]"><SimpleSelect value={status || "all"} onChange={(v) => setStatus(v === "all" ? "" : v)}
          options={[{ value: "all", label: "All statuses" }, ...(me?.enums?.statuses || []).map((s) => ({ value: s, label: label(s) }))]} /></div>
        <div className="w-56 max-md:w-full"><SimpleSelect value={category || "all"} onChange={(v) => setCategory(v === "all" ? "" : v)}
          options={[{ value: "all", label: "All categories" }, ...cats.map((c) => ({ value: c.code, label: c.label }))]} /></div>
        <div className="text-xs text-slate-500">{rows.length} part(s) · latest revision shown</div>
      </div>
      <DataTable rows={rows} testid="design-items-table" onRowClick={(r) => setOpenId(r.id)}
        columns={[
          { key: "number", title: "Part No", cellClass: "mono font-semibold" },
          { key: "revision", title: "Rev", cellClass: "mono" },
          { key: "name", title: "Name", cellClass: "max-w-xs truncate" },
          { key: "category", title: "Category" },
          { key: "make_buy", title: "Make/Buy", render: (r) => label(r.make_buy) },
          { key: "material", title: "Material" },
          { key: "size", title: "Size" },
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} stage={r.review_stage} /> },
          { key: "updated_at", title: "Updated", render: (r) => fmtDate(r.updated_at) },
        ]} />

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New item</DialogTitle></DialogHeader>
          <ItemForm form={form} setForm={setForm} categories={cats} showNumber={can("approve")} />
          <DuplicateHints form={form} />
          <ErrorList errors={errors} />
          <label className="text-xs flex items-center gap-2"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="item-ack-dup" />I reviewed similar items — create a new part number anyway</label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={create} disabled={!form.name || !form.category} data-testid="item-create">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
          <SheetHeader><SheetTitle>Item</SheetTitle></SheetHeader>
          {openId && <ItemDetail key={openId} id={openId} onChanged={load} onOpen={setOpenId} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
